import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { isEqual } from 'lodash';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import {
  fetchAppliedJobs,
  fetchJobs,
  fetchJobsPublic,
  fetchNextAppliedJobs,
  fetchNextJobs,
  fetchNextJobsPublic,
  fetchNextSavedJobs,
  fetchSavedJobs,
  filterJobsCount,
  getCounters,
  getSearchSuggestion
} from '../../actions/jobs';
import { jobsPageSize, responsiveSizeJobs, jobFiltersConfig, maxMobileSize } from '../../utils/constants';
import TabsV2 from '../../Components/common/Tabs/TabsV2';
import { SearchJobV2Icon, BookmarkIcon, BellIcon, BellRingIcon, TickIcon } from '../../Components/common/Icons';
import StyledPaginationPanel from '../../Components/common/StyledPaginationPanel/StyledPaginationPanel';
import classes from './jobs.module.scss';
import { useOnScreen, usePrevious, useWindowSize, useDebounce } from '../../utils/hooks';
// eslint-disable-next-line import/no-cycle
import JobList from '../../Components/pages/jobs/JobList/JobList';
import GlobalFilters from '../../Components/common/Filters/GlobalFilters/GlobalFilters';
import GlobalSearchandLocation from '../../Components/molecules/GlobalSearchandLocation/GlobalSearchandLocation';
import { fetchSmartLocations } from '../../actions/locations';
import colors from '../../scss/colors';
import QuickApply from '../../Components/quickapply';
import QuickApplyV2 from '../../Components/quickapplyv2';
import { getFullLocationString, setSearchParamConstructor } from '../../utils/helpers';
import AppTrackingIcon from '../../Components/common/Icons/benefits/AppTrackingIcon';
import MainButton from '../../Components/common/buttons/MainButton';
import NoResults from '../../Components/common/NoResults/NoResults';
import { buttonVariants } from '../../Components/common/buttons/types';
import Subtabs from '../../Components/pages/jobs/Subtabs/Subtabs';
import EasyApplyIcon from '../../Components/common/Icons/benefits/EasyAppyIcon';
import { createNewJobAlert, getAllJobAlerts } from '../../actions/jobAlerts';
// eslint-disable-next-line import/no-cycle
import FullScreenJobPage from '../../Components/pages/jobs/FullScreenJobPage/FullScreenJobPage';
import HideOnScroll from '../../Components/common/HideOnScroll/HideOnScroll';
import WithCloseIcon from '../../Components/common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import BaseModal from '../../Components/common/ModalWindows/BaseModal';
import UnloggedAlertPopUp from '../../Components/common/ModalWindows/UnloggedAlertPopUp/UnloggedAlertPopUp';
import { loginRequest } from '../../authConfig';
import { PAGE_TYPES } from '../../utils/enums';
import MaxAlertsQTYPopUp from '../../Components/common/ModalWindows/MaxAlertsQTYPopUp/MaxAlertsQTYPopUp';
import { NavigationBar } from '../../Components/navigationbar/NavigationBar';
import { Sidebar } from '../../Components/Layout/components';
import { fetchUserData } from '../../actions/user';
import { NotificationsActiveRounded, NotificationsNoneOutlined } from '@mui/icons-material';

export const tabs = {
  PUBLIC_JOBS: 'publicjobs',
  MY_JOBS: 'myjobs'
};

export const subTabs = {
  SAVED: 'saved',
  APPLIED: 'applied'
};

const Jobs = ({ isFullPage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const jobs = useSelector((state) => state.jobs.response);
  const paginationData = useSelector((state) => state.jobs.pagination);
  const counters = useSelector((state) => state.jobs.counters);
  const pendingCounters = useSelector((state) => state.jobs.pendingCounters);
  const infinityIsLoading = useSelector((state) => state.jobs.pendingInfinityScroll);
  const paginationPagesLimit = useSelector((state) => state.jobs.pagination.TotalPages);
  const pendingFetchJobsList = useSelector((state) => state.jobs.pendingFlag);
  const jobAlerts = useSelector((state) => state.jobAlerts.alertsForFilters);
  const jobAlertsPending = useSelector((state) => state.jobAlerts.pendingFlag);
  const jobAlertError = useSelector((state) => state.jobAlerts.createError);
  const industries = useSelector((state) => state.constFromApi.industries);
  const functionsDirections = useSelector((state) => state.constFromApi.functionsDirections);
  const prevPendingFetchJobsList = usePrevious(pendingFetchJobsList);
  const prevPendingJobAlertsPending = usePrevious(jobAlertsPending);
  const initIntersactionRequestRef = useRef(false);
  const IntersactionMarkerRef = useRef(null);
  const isIntersecting = useOnScreen(IntersactionMarkerRef, {
    threshold: 0.1
  });
  const initState = {
    initPage: Number(searchParams.get('page')) || 1,
    initTab: searchParams.get('tab') || tabs.PUBLIC_JOBS,
    initSubTab: searchParams.get('subTab') || subTabs.SAVED,
    initSearchTxt: searchParams.get('searchTxt') || '',
    initLocation: searchParams.get('location') || '',
    initIndustries: searchParams.getAll('industries') || [],
    initFunctions: searchParams.getAll('functions') || [],
    initContractTypes: searchParams.getAll('contractTypes') || [],
    initRemoteTypes: searchParams.getAll('remoteTypes') || [],
    initDate: searchParams.get('date') || '',
    initCity: searchParams.get('city') || '',
    initState: searchParams.get('state') || '',
    initCountry: searchParams.get('country') || ''
  };
  const [recentSearches, setRecentSearches] = useState([]);
  const [openPopUp, setOpenPop] = useState(null);
  const [placeholderMobile, setPlaceholderMobile] = useState(false);
  const [searchFocused, setSearchFocused] = useState(width > responsiveSizeJobs);
  const [filters, setFilters] = useState({
    page: initState.initPage,
    tab: initState.initTab,
    subTab: initState.initSubTab,
    searchTxt: initState.initSearchTxt,
    city: initState.initCity,
    country: initState.initCountry,
    state: initState.initState,
    location: initState.initLocation,
    industries: initState.initIndustries,
    functions: initState.initFunctions,
    contractTypes: initState.initContractTypes,
    remoteTypes: initState.initRemoteTypes,
    date: initState.initDate,
    isReset: false,
    suggestionTriggered: false
  });
  const [searchTrigger, setSearchTrigger] = useState(true);
  const [lastSearchedFilters, setLastSearchedFilters] = useState(null);
  const [resultsLabel, setResultsLabel] = useState('');
  const [alertVisibility, setAlertVisibility] = useState(false);
  const [isAlertSet, setIsAlertSet] = useState(false);
  const locationRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const userId = localStorage.getItem('UserId');
  const userData = useSelector((state) => state.user?.profile);
  const baseTabs = [
    {
      title: t('dashboard.jobs.jobsearch'),
      value: tabs.PUBLIC_JOBS,
      icon: <SearchJobV2Icon fill={colors.NAVY_BLUE} />
    }
  ];
  const authTabs = [
    {
      title: t('dashboard.jobs.myjobs'),
      value: tabs.MY_JOBS,
      icon: <BookmarkIcon width={20} height={20} />
    }
  ];
  const size = useWindowSize();
  const SIDEBAR_W_EXPANDED = 220;
  const SIDEBAR_W_COLLAPSED = 64;
  const [collapsed, setCollapsed] = useState(false);
  const tabsSet = useMemo(() => (isAuthenticated ? [...baseTabs, ...authTabs] : baseTabs), [isAuthenticated]);

  // ---- Mobile-safe layout calculations ----
  const isMobile = size.width < maxMobileSize;
  const sidebarW = isMobile ? 0 : collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
  // Sidebar is only ever mounted on non-mobile viewports. On mobile it is not
  // rendered at all, so there must be no reserved margin for it either.
  const showSidebar = !isMobile;
  const contentMarginLeft = !isMobile && !isAuthenticated ? sidebarW : 0;

  const handleSearchParams = () => {
    const { id } = params;
    const action = searchParams.get('action');
    const filterResult = setSearchParamConstructor({ action, id, ...filters }, 'value');
    const newParams = new URLSearchParams(searchParams);
    const incomingParams = new URLSearchParams(filterResult);
    incomingParams.forEach((value, key) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const closeFullJobPage = () => {
    searchParams.delete('id');
    setSearchParams(searchParams);
    if (isAuthenticated) {
      navigate('/dashboard/jobs');
    } else {
      navigate('/jobs');
    }
  };

  const changeTab = useCallback(
    (tabName) => {
      setSearchTrigger(true);
      setFilters((prev) => ({ ...prev, page: 1, tab: tabName.value }));
    },
    [setSearchTrigger, setFilters]
  );

  const changePage = (page) => {
    closeFullJobPage();
    setFilters((prev) => ({ ...prev, page }));
    setSearchTrigger(true);
  };

  const isFiltersSelected = () =>
    filters.searchTxt !== '' ||
    filters.location !== '' ||
    filters.city !== '' ||
    filters.country !== '' ||
    filters.industries?.length !== 0 ||
    filters.functions?.length !== 0 ||
    filters.contractTypes?.length !== 0 ||
    filters.remoteTypes?.length !== 0 ||
    filters.date !== '';

  const getResultsLabeAlignment = () => {
    if (width > responsiveSizeJobs) return 'space-between';
    if (resultsLabel === t('dashboard.jobs.latestjobs')) return 'left';
    return 'center';
  };

  const paramsForSearch = (filter) => ({
    searchTxt: filter.searchTxt,
    location: filter.location,
    city: filter.city,
    state: filter.state,
    country: filter.country,
    industries: filter.industries,
    functions: filter.functions,
    contractTypes: filter.contractTypes,
    remoteTypes: filter.remoteTypes,
    date: filter.date
  });

  const getFiltersString = () => {
    const f = lastSearchedFilters;
    if (!f) return '';
    return `
    ${f.searchTxt ? `${f.searchTxt}, ` : ''}
    ${Array.isArray(f.industries) && f.industries?.length ? f.industries.reduce((res, item) => `${res + (item.label || item)}, `, '') : ''}
    ${Array.isArray(f.functions) && f.functions?.length ? f.functions.reduce((res, item) => `${res + (item.label || item)}, `, '') : ''}
    ${Array.isArray(f.contractTypes) && f.contractTypes?.length ? f.contractTypes.reduce((res, item) => `${res + (item.label || item)}, `, '') : ''}
    ${Array.isArray(f.remoteTypes) && f.remoteTypes?.length ? f.remoteTypes.reduce((res, item) => `${res + (item.label || item)}, `, '') : ''}
    ${f.city ? `${t('common.general.in')} ${getFullLocationString(f.city, f.state, f.country)}, ` : ''}
    ${f.location && !f.city ? `${t('common.general.in')} ${f.location}, ` : ''}
  `
      .trim()
      .slice(0, -1);
  };

  useEffect(() => {
    if (userId && !userData && isAuthenticated) {
      dispatch(fetchUserData(userId));
    }
  }, [userId]);

  useEffect(() => {
    const resultPage = {
      active: 1,
      limit: jobsPageSize
    };
    if (filters.isReset) {
      if (isAuthenticated) {
        dispatch(fetchJobs(resultPage, paramsForSearch(filters)));
        setFilters({ ...filters, isReset: false });
      } else {
        dispatch(fetchJobsPublic(resultPage, paramsForSearch(filters)));
        setFilters({ ...filters, isReset: false });
      }
    }
  }, [filters]);

  useEffect(() => {
    const resultPage = {
      active: filters.page,
      limit: jobsPageSize
    };
    if (filters.tab === tabs.PUBLIC_JOBS) {
      if (isAuthenticated && searchTrigger) {
        dispatch(fetchJobs(resultPage, paramsForSearch(filters)));
      }
      if (!isAuthenticated && searchTrigger) {
        dispatch(fetchJobsPublic(resultPage, paramsForSearch(filters)));
      }
    }
    setSearchTrigger(false);
    handleSearchParams();
  }, [filters, searchTrigger]);

  const refineFilterForCompare = () => ({
    city: filters.city,
    contractTypes: filters.contractTypes.map((contract) => contract.value || contract),
    country: filters.country,
    functions: filters.functions.map((functions) => functions.value || functions),
    industries: filters.industries.map((industry) => industry.value || industry),
    location: filters.location,
    remoteTypes: filters.remoteTypes?.length ? filters.remoteTypes.map((type) => type.value || type) : ['2'],
    searchTxt: filters.searchTxt,
    state: filters.state
  });

  const jobAlertEqualizer = () => {
    const filterObj = refineFilterForCompare();
    const isAlertExist = jobAlerts.some((alert) => isEqual(alert, filterObj));
    setIsAlertSet(isAlertExist);
  };

  const handleFilters = (filter) => {
    setFilters({ ...filter, isReset: false });
    dispatch(filterJobsCount(filter));
    jobAlertEqualizer();
  };

  const searchHandler = () => {
    const resultPage = {
      active: filters.page,
      limit: jobsPageSize
    };
    setLastSearchedFilters(filters);
    const hasFullLocation = Boolean(filters.location || (filters.city && filters.state && filters.country));
    if (filters.searchTxt && hasFullLocation) {
      const list = Array.isArray(recentSearches) ? recentSearches : [];
      const name = `${filters.searchTxt} Jobs in ${
        filters.location ?? `${filters.city}, ${filters.state}, ${filters.country}`
      }`;
      const deduped = list.filter((item) => item?.Name !== name);
      const next = [{ Name: name }, ...deduped].slice(0, 5);
      setRecentSearches(next);
    }
    if (isAuthenticated) {
      dispatch(fetchJobs(resultPage, paramsForSearch(filters)));
    } else {
      dispatch(fetchJobsPublic(resultPage, paramsForSearch(filters)));
    }
    dispatch(filterJobsCount(filters));
  };

  useEffect(() => {
    if (recentSearches?.length) {
      localStorage.setItem('userRecentSearches', JSON.stringify(recentSearches));
    }
  }, [recentSearches]);

  useEffect(() => {
    if (!pendingFetchJobsList && prevPendingFetchJobsList && filters.tab === tabs.PUBLIC_JOBS) {
      if (isFiltersSelected()) {
        setResultsLabel(
          width > responsiveSizeJobs
            ? `${paginationData?.TotalRecords} ${filters.searchTxt ?? ''} ${t('dashboard.jobs.job')} ${
                filters.location || (filters.city && filters.country)
                  ? `in ${getFullLocationString(filters.city, filters.state, filters.country, filters.location)}`
                  : ''
              }`
            : `${paginationData?.TotalRecords} ${t('dashboard.jobs.job')}`
        );
        setAlertVisibility(true);
      } else {
        setResultsLabel(t('dashboard.jobs.latestjobs'));
        setAlertVisibility(false);
      }
    }
  }, [pendingFetchJobsList, infinityIsLoading]);

  useEffect(() => {
    const resultPage = {
      active: filters.page,
      limit: jobsPageSize
    };
    if (filters.tab === tabs.MY_JOBS) {
      if (filters.subTab === subTabs.APPLIED) {
        dispatch(fetchAppliedJobs(resultPage, {}, {}));
      } else {
        dispatch(fetchSavedJobs(resultPage, {}, {}));
      }
    }
    setSearchTrigger(false);
    handleSearchParams();
  }, [filters]);

  const resetHandler = useCallback(() => {
    const preservedParams = new URLSearchParams();
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    utmKeys.forEach((key) => {
      const val = searchParams.get(key);
      if (val) preservedParams.set(key, val);
    });
    setSearchParams(preservedParams);
    setLastSearchedFilters(null);
    setFilters({
      page: initState.initPage,
      tab: initState.initTab,
      subTab: initState.initSubTab,
      searchTxt: '',
      location: '',
      city: '',
      state: '',
      country: '',
      industries: [],
      functions: [],
      contractTypes: [],
      remoteTypes: [],
      date: '',
      isReset: true,
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || ''
    });
    dispatch(filterJobsCount({}));
    setPlaceholderMobile(true);
  }, [searchParams, setSearchParams, setFilters, dispatch, setPlaceholderMobile]);

  const resetIndividual = (type) => {
    setFilters({ ...filters, [type]: [], isReset: true });
    dispatch(filterJobsCount({}));
  };

  const fetchLocation = useDebounce((e) => {
    dispatch(fetchSmartLocations(e.target.value));
  }, 500);

  const fetchSearchSuggestions = useDebounce((e) => {
    dispatch(getSearchSuggestion(e.target.value));
  }, 500);

  const fetchCounters = async () => {
    dispatch(getCounters());
  };

  const locationHandler = (newValue) => {
    if (newValue?.length) {
      const [city, state, country] = newValue.trim().split(',');
      setFilters({ ...filters, city, state, country, location: '', isReset: false });
      if (width < responsiveSizeJobs) setSearchFocused(false);
    }
  };

  const searchInputHandler = (newValue) => {
    setFilters({ ...filters, searchTxt: newValue, isReset: false });
    if (newValue?.length > 0) locationRef.current.focus();
  };

  const onInputHandler = (e) => {
    setFilters({ ...filters, city: '', state: '', country: '', location: e.target.value, isReset: false });
    if (e.target.value.trim()?.length > 2) {
      fetchLocation(e);
    }
  };

  const onKeyDownSearch = (e) => {
    setFilters({ ...filters, searchTxt: e.target.value });
    if (e.target.value.trim()?.length > 2) {
      fetchSearchSuggestions(e);
    }
  };

  const clearSearchHandler = () => {
    setFilters({ ...filters, searchTxt: '', isReset: true });
  };

  const clearLocationHandler = () => {
    setFilters({ ...filters, city: '', state: '', country: '', location: '', isReset: true });
  };

  const locationValue = () => {
    if (filters?.city && filters.state && filters.country) {
      return `${filters?.city}, ${filters?.state}, ${filters?.country}`;
    }
    return '';
  };

  const suggestionHandler = (newValue) => {
    const suggestion = newValue.trim().split('Jobs in');
    const isLocationSplit = suggestion[1].includes(',');
    const [city, state, country] = suggestion[1].trim().split(',');
    setFilters({
      ...filters,
      searchTxt: suggestion[0].trim(),
      location: !isLocationSplit ? suggestion[1].trim() : '',
      city: isLocationSplit ? city?.trim() : '',
      state: isLocationSplit ? state?.trim() : '',
      country: isLocationSplit ? country?.trim() : '',
      suggestionTriggered: true
    });
    setPlaceholderMobile(false);
  };

  const setUnloggedAlert = () => {
    localStorage.setItem('type', 'setAlert');
    localStorage.setItem('page', PAGE_TYPES.JOBS);
    localStorage.setItem('queriesForPage', window.location.search);
    instance
      .loginRedirect({
        ...loginRequest,
        redirectUri: `${window.location.origin}/`
      })
      .catch((e) => {
        console.error(e);
      });
  };

  useEffect(() => {
    if (filters?.searchTxt?.length && filters?.country) {
      searchHandler();
    }
  }, [filters?.searchTxt, filters?.country]);

  useEffect(() => {
    if (filters?.suggestionTriggered) {
      searchHandler();
    }
  }, [filters?.suggestionTriggered]);

  useEffect(() => {
    if (
      isIntersecting &&
      width < responsiveSizeJobs &&
      !initIntersactionRequestRef.current &&
      paginationData &&
      paginationData.PageNumber + 1 <= paginationData?.TotalPages
    ) {
      const resultPage = {
        active: paginationData.PageNumber + 1,
        limit: jobsPageSize
      };
      switch (filters.tab) {
        case tabs.PUBLIC_JOBS: {
          if (isAuthenticated) {
            dispatch(fetchNextJobs(resultPage, paramsForSearch(filters)));
          } else {
            dispatch(fetchNextJobsPublic(resultPage, paramsForSearch(filters)));
          }
          break;
        }
        case tabs.MY_JOBS: {
          if (filters.subTab === subTabs.APPLIED) {
            dispatch(fetchNextAppliedJobs(resultPage, {}, {}));
          } else {
            dispatch(fetchNextSavedJobs(resultPage, {}, {}));
          }
          break;
        }
        default:
          return;
      }
    }
    initIntersactionRequestRef.current = false;
  }, [isIntersecting]);

  useEffect(() => {
    if (width > responsiveSizeJobs) setSearchFocused(true);
  }, [width]);

  const onFocusHandler = () => {
    setSearchFocused(true);
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getAllJobAlerts());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refineFilterForCompare();
    jobAlertEqualizer();
  }, [jobAlerts, filters]);

  const onBlurSearch = () => {};

  const onBlurLocation = () => {
    if (filters.location || (filters?.city && filters.state && filters.country)) setSearchFocused(false);
  };

  const filterVisible = () => {
    if (width < responsiveSizeJobs) {
      return !!(filters.searchTxt || (filters.location || (filters.city && filters.state && filters.country)));
    }
    return true;
  };

  const handleCreateAlert = () => {
    const remoteWork = () => {
      if (filters.remoteTypes?.length > 1) return 2;
      if (filters?.remoteTypes[0]?.value === '0' || filters?.remoteTypes[0] === '0') return 0;
      if (filters?.remoteTypes[0]?.value === '1' || filters?.remoteTypes[0] === '1') return 1;
      return 2;
    };
    const data = {
      UserId: localStorage.getItem('UserId'),
      Keyword: filters.searchTxt,
      jobAlertIndustries: filters.industries.map((filter) => ({
        IndustryId: filter?.key || industries?.find((item) => item.value === filter).key
      })),
      jobAlertJobFunctions: filters.functions.map((filter) => ({
        JobFunctionId: filter?.key || functionsDirections?.find((item) => item.value === filter).key
      })),
      jobAlertContractTypes: filters.contractTypes.map((filter) => ({
        JobType:
          (filter.value && filter.value.charAt(0).toUpperCase() + filter.value.slice(1)) ||
          filter.charAt(0).toUpperCase() + filter.slice(1)
      })),
      Frequency: 'weekly',
      Location: filters.location,
      City: filters.city,
      State: filters.state,
      Country: filters.country,
      JobPeriodId: filters.date || null,
      RemoteWork: remoteWork()
    };
    if (jobAlerts?.length >= 5) {
      return setOpenPop({ name: 'maxAlerts' });
    }
    dispatch(createNewJobAlert(data));
    return true;
  };

  useEffect(() => {
    fetchCounters();
  }, []);

  useEffect(() => {
    if (!jobAlertsPending && prevPendingJobAlertsPending && searchParams.get('action') === 'setAlert') {
      handleCreateAlert();
      handleSearchParams();
    }
  }, [jobAlertsPending]);

  const popUpModals = {
    unloggedAlert: <UnloggedAlertPopUp alertQueries={getFiltersString()} redirectAction={setUnloggedAlert} />,
    maxAlerts: <MaxAlertsQTYPopUp />
  };

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const subTabParam = searchParams.get('subTab');
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (tabParam && tabParam !== prev.tab) newFilters.tab = tabParam;
      if (subTabParam && subTabParam !== prev.subTab) newFilters.subTab = subTabParam;
      if (newFilters.tab !== prev.tab || newFilters.subTab !== prev.subTab) return newFilters;
      return prev;
    });
  }, [searchParams]);

  useEffect(() => {
    if (jobAlertError) setOpenPop({ name: 'maxAlerts' });
  }, [jobAlertError]);

  return (
    <div className="w-full max-w-7xl mx-auto px-6 xl:px-8 pb-8 pt-2 overflow-x-hidden">
      <div className="flex mx-auto max-w-[1280px] gap-2 w-full min-w-0">
        <div className="w-full min-w-0">
          {showSidebar && (
            <div className="sticky top-0 p-0 mb-0">
              {!isAuthenticated && <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />}
            </div>
          )}
          {params.id && isFullPage ? (
            <>
              {showSidebar && isAuthenticated && <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />}
              <section
                style={{
                  marginLeft: contentMarginLeft,
                  transition: 'margin-left 0.2s ease-in-out',
                  minHeight: '100vh'
                }}
                className="flex flex-col min-w-0 overflow-x-hidden bg-[#F0F4F7]"
              >
                <div className="page-enter flex flex-col flex-1 min-w-0 lg:p-6">
                  <FullScreenJobPage id={params.id} onClose={closeFullJobPage} forMobile={width <= responsiveSizeJobs} />
                </div>
              </section>
            </>
          ) : (
            <section
              style={{
                marginLeft: contentMarginLeft,
                transition: 'margin-left 0.2s ease-in-out',
                minHeight: '100vh'
              }}
              className="flex flex-col min-w-0 overflow-x-hidden bg-[#F0F4F7] pt-14 pb-20 md:pb-0"
            >
              <div className="page-enter flex flex-col flex-1 min-w-0 lg:p-6">
                {isAuthenticated && (
                  <TabsV2 tabs={tabsSet} currentTab={filters.tab} setTab={changeTab} withDivider={width > responsiveSizeJobs} />
                )}
                {filters.tab === tabs.PUBLIC_JOBS && (
                  <>
                    {width < responsiveSizeJobs && <div style={{ height: '26px' }} />}
                    <div className="w-full pb-2 p-0 min-w-0">
                      <GlobalSearchandLocation
                        custom
                        searchHandler={searchHandler}
                        setSearchValue={searchInputHandler}
                        searchValue={filters.searchTxt}
                        locationValue={filters?.location || locationValue()}
                        setLocationValue={locationHandler}
                        onKeyDownHandler={onInputHandler}
                        onKeyDownSearch={onKeyDownSearch}
                        clearLocationHandler={clearLocationHandler}
                        clearSearchHandler={clearSearchHandler}
                        suggestionHandler={suggestionHandler}
                        onFocusHandler={onFocusHandler}
                        isSearchFocused={searchFocused}
                        onBlurSearch={onBlurSearch}
                        onBlurLocation={onBlurLocation}
                        placeholderMobileHandler={setPlaceholderMobile}
                        placeholderMobile={placeholderMobile}
                        locationRef={locationRef}
                        searchRef={searchRef}
                        searchButtonVariant={buttonVariants.PRIMARY}
                        showWebVersionInMobile
                      />
                    </div>

                    <GlobalFilters
                      reset
                      filters={jobFiltersConfig}
                      changeHandlers={handleFilters}
                      values={filters}
                      isContainerBg
                      resetHandler={resetHandler}
                      resetIndividual={resetIndividual}
                      footerHandler={searchHandler}
                      resetVisibility={alertVisibility}
                    />
                  </>
                )}

                <>
                  {filters.tab === tabs.MY_JOBS && (
                    <Subtabs
                      counters={{ saved: counters.SavedJobs, applied: counters.AppliedJobs }}
                      pendingCounters={pendingCounters}
                      subtabs={subTabs}
                      currentSubtab={filters.subTab}
                      setSubTab={setFilters}
                    />
                  )}
                  {!pendingFetchJobsList &&
                    filters.tab === tabs.MY_JOBS &&
                    filters.subTab === subTabs.SAVED &&
                    jobs?.length === 0 && (
                      <div className={classes.noresults}>
                        <NoResults
                          isMobile={width <= responsiveSizeJobs}
                          icon={
                            <div className={classes.icon}>
                              <EasyApplyIcon />
                            </div>
                          }
                          primaryTxt={t('dashboard.jobs.nothingsaved')}
                          secondaryTxt={t('dashboard.jobs.nothingsavedsecondary')}
                        >
                          <MainButton
                            rounded
                            forceWidth={width > responsiveSizeJobs ? '220px' : '300px'}
                            variant={buttonVariants.PRIMARY}
                            action={() => changeTab(tabsSet[0])}
                          >
                            <div className={classes.btnContainer}>{t('dashboard.jobs.browsejobsforme')}</div>
                          </MainButton>
                        </NoResults>
                      </div>
                    )}
                  {!pendingFetchJobsList && filters.tab === tabs.PUBLIC_JOBS && jobs?.length === 0 && (
                    <div className={classes.noresults}>
                      <NoResults
                        isMobile={width <= responsiveSizeJobs}
                        icon={
                          <div className={classes.icon}>
                            <AppTrackingIcon />
                          </div>
                        }
                        primaryTxt={t('dashboard.jobs.noresults')}
                        secondaryTxt={
                          !isAlertSet ? (
                            <Trans i18nKey="dashboard.jobs.noresultssetalert" values={{ filters: getFiltersString() }} />
                          ) : (
                            `${t('dashboard.jobs.noresultssettedalert')}${getFiltersString()}`
                          )
                        }
                      >
                        <MainButton
                          rounded
                          forceWidth={width > responsiveSizeJobs ? '220px' : '300px'}
                          disabled={isAlertSet}
                          variant={isAlertSet ? buttonVariants.DISABLED : buttonVariants.PRIMARY}
                          action={() => {
                            if (!isAuthenticated) {
                              setUnloggedAlert();
                              return;
                            }
                            handleCreateAlert();
                          }}
                        >
                          <div className={classes.btnContainer}>
                            {!isAuthenticated && t('common.general.getstarted')}
                            {jobAlertsPending ? (
                              <CircularProgress sx={{ color: colors.DARKEST_GREY }} />
                            ) : (
                              isAuthenticated && (
                                <>
                                  {!isAlertSet ? <NotificationsNoneOutlined /> : <NotificationsActiveRounded />}
                                  {!isAlertSet ? t('jobs.alerts.setjobalert') : t('jobs.alerts.alertcreated')}
                                </>
                              )
                            )}
                          </div>
                        </MainButton>
                      </NoResults>
                    </div>
                  )}
                  {filters.tab === tabs.PUBLIC_JOBS && !!jobs?.length && (
                    <div
                      className={width > responsiveSizeJobs ? classes.resultLabelDesktop : classes.resultsLabel}
                      style={{ justifyContent: getResultsLabeAlignment() }}
                    >
                      <span>{resultsLabel}</span>
                      {!!alertVisibility && width > responsiveSizeJobs && (
                        <MainButton
                          size="xmedium"
                          variant={buttonVariants.TEXT}
                          action={() => {
                            if (!isAuthenticated) {
                              setOpenPop({ name: 'unloggedAlert', params: { filters } });
                              return;
                            }
                            if (!isAlertSet) handleCreateAlert();
                          }}
                        >
                          <span className={classes.resultLabelIcon}>
                            {!isAlertSet ? (
                              <BellRingIcon color={colors.WHITE} width="20" height="20" />
                            ) : (
                              <TickIcon color={colors.WHITE} width="20" height="20" />
                            )}
                          </span>
                          <span className="text-[#6b6c6f]">
                            {!isAlertSet ? t('jobs.alerts.set') : t('jobs.alerts.alertcreated')}
                          </span>
                        </MainButton>
                      )}
                    </div>
                  )}
                  <JobList tabs={{ tab: filters.tab, subTab: filters.subTab }} jobs={jobs} pending={pendingFetchJobsList} />
                  {width > responsiveSizeJobs && (
                    <div className={classes.paginationContainer}>
                      <StyledPaginationPanel total={paginationPagesLimit} active={filters.page} setActive={changePage} />
                    </div>
                  )}
                </>
                <div ref={IntersactionMarkerRef} style={{ margin: '8px auto' }}>
                  {infinityIsLoading && <CircularProgress sx={{ color: colors.NAVY_BLUE }} />}
                </div>
                {!!alertVisibility && filters.tab === tabs.PUBLIC_JOBS && width < responsiveSizeJobs && jobs?.length !== 0 && (
                  <HideOnScroll appear direction="up">
                    <div className={classes.createJobAlertButton}>
                      <MainButton
                        forceWidth="100%"
                        size="xsmall"
                        variant={isAlertSet ? buttonVariants.LIGHT_GREY : buttonVariants.NAVY_BLUE}
                        action={() => {
                          if (!isAuthenticated) {
                            setOpenPop({ name: 'unloggedAlert', params: { filters } });
                            return;
                          }
                          if (!isAlertSet) handleCreateAlert();
                        }}
                        sx={{
                          gap: '14px',
                          fontWeight: 'normal'
                        }}
                      >
                        <span className={classes.resultLabelIcon}>
                          {!isAlertSet ? (
                            <BellRingIcon color={colors.WHITE} width="20" height="20" />
                          ) : (
                            <TickIcon color={colors.WHITE} width="20" height="20" />
                          )}
                        </span>
                        <span className="text-[#6b6c6e]">{!isAlertSet ? t('jobs.alerts.set') : t('jobs.alerts.alertcreated')}</span>
                      </MainButton>
                    </div>
                  </HideOnScroll>
                )}
                {searchParams.get('action') === 'apply' &&
                  !params.id &&
                  !isFullPage &&
                  (isAuthenticated ? <QuickApply /> : <QuickApplyV2 />)}
              </div>
              <BaseModal
                open={openPopUp}
                fullscreen={false}
                closeHandler={() => setOpenPop(null)}
                title="&nbsp;"
                hideFooter
                hideHeader
                withSeparator={false}
                marginLeftRight={1}
                paperWidth={width > responsiveSizeJobs ? '554px' : '100%'}
                paperStyles={width <= responsiveSizeJobs ? { margin: '15px', borderRadius: '10px' } : { borderRadius: '40px' }}
              >
                <WithCloseIcon close={() => setOpenPop('')} y={6}>
                  {popUpModals[openPopUp?.name]}
                </WithCloseIcon>
              </BaseModal>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Jobs;